export default (useHTTP, { useApiModel, useConstantList }) =>
  function getModelStore(options = {}) {
    const $http = useHTTP();
    const $apiModel = useApiModel();
    const { setDefaultOptions, isPostRequest, getModelOptionsByType } = options;

    function hasApis() {
      return !!Object.keys($apiModel.apis).length;
    }

    function getApiUrl(model, operation, apiAction) {
      return apiAction
        ? $apiModel.getApi(model, operation, apiAction)
        : $apiModel.getApi(model, operation);
    }

    async function getModelRequest(model, data, config, self) {
      const { useMock, useFormData, usePost, ...customOptions } = config;

      if (useFormData) {
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(
            key,
            Array.isArray(value) ? JSON.stringify(value) : value
          );
        }
        data = formData;
      }

      const defaultOptions = setDefaultOptions
        ? await setDefaultOptions(model, self) // 可扩展
        : {};

      config = Object.assign(
        useMock || self.$mock
          ? { baseURL: `/mock${$http.defaults.baseURL}` }
          : {},
        defaultOptions,
        customOptions
      );

      return {
        usePost,
        data,
        config
      };
    }

    return {
      beforeMount() {
        this.$nextTick(() => {
          if (!hasApis()) {
            console.warn('BalmUIPro api model undefined');
          }
        });
      },
      methods: {
        // Basic
        async $createModel(model, data = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'create', apiAction);
          const modelRequest = await getModelRequest(
            model,
            data,
            options,
            this
          );
          return $http.post(apiUrl, modelRequest.data, modelRequest.config);
        },
        async $deleteModel(model, data = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'delete', apiAction);
          const modelRequest = await getModelRequest(
            model,
            data,
            options,
            this
          );
          return $http.post(apiUrl, modelRequest.data, modelRequest.config);
        },
        async $updateModel(model, data = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'update', apiAction);
          const modelRequest = await getModelRequest(
            model,
            data,
            options,
            this
          );
          return $http.post(apiUrl, modelRequest.data, modelRequest.config);
        },
        async $getModel(model, params = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'read', apiAction);
          const modelRequest = await getModelRequest(
            model,
            params,
            options,
            this
          );
          return modelRequest.usePost ||
            (isPostRequest &&
              isPostRequest({ model, apiAction, apiUrl, config }))
            ? $http.post(apiUrl, modelRequest.data, modelRequest.config)
            : $http.get(apiUrl, {
                params: modelRequest.data,
                ...modelRequest.config
              });
        },
        // Extended
        async $getModelList(model, params = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'read', 'list');
          const modelRequest = await getModelRequest(
            model,
            params,
            options,
            this
          );
          return modelRequest.usePost ||
            (isPostRequest &&
              isPostRequest({ model, apiAction: 'list', apiUrl, config }))
            ? $http.post(apiUrl, modelRequest.data, modelRequest.config)
            : $http.get(apiUrl, {
                params: modelRequest.data,
                ...modelRequest.config
              });
        },
        async $getModelDetail(model, params = {}, config = {}) {
          const { apiAction, ...options } = config;
          const apiUrl = getApiUrl(model, 'read', 'detail');
          const modelRequest = await getModelRequest(
            model,
            params,
            options,
            this
          );
          return modelRequest.usePost ||
            (isPostRequest &&
              isPostRequest({ model, apiAction: 'detail', apiUrl, config }))
            ? $http.post(apiUrl, modelRequest.data, modelRequest.config)
            : $http.get(apiUrl, {
                params: modelRequest.data,
                ...modelRequest.config
              });
        },
        async $getModelOptions(model, params = {}, config = {}) {
          let result = [];

          const models = model.split(':');
          const [modelType, modelName] = models;
          if (modelName) {
            if (modelType === 'local') {
              result = useConstantList(modelName);
            } else {
              result = getModelOptionsByType
                ? await getModelOptionsByType(modelType, {
                    model: modelName,
                    params,
                    config,
                    $store: this
                  })
                : [];
            }
          } else {
            result = await this.$getModel(
              model,
              params,
              Object.assign({}, config, {
                apiAction: config.apiAction
                  ? `${config.apiAction}Options`
                  : 'options'
              })
            );
          }

          return result;
        }
      }
    };
  };
